import { Box, Select, Checkbox } from '@ff-it/ui';
import { ReactNode, useId } from 'react';
import type { ActionAvailability, CatalogAction, TargetType } from './types';

import * as styles from './ActionOption.css';
import { activityFieldProps, useOptions } from 'components/fields';
import { outletOrServiceCategoryFieldProps } from 'modules/supplier/outletOrServiceCategory/OutletOrServiceCategoryField';
import { EmbeddedOutletOrServiceCategory } from 'modules/supplier/outletOrServiceCategory/types';
import { Vendor } from 'modules/supplier/vendor/types';
import { vendorFieldProps } from 'modules/supplier/vendor/VendorField';
import { Activity } from 'types';

type ActionOptionProps = ActionAvailability & {
  model: string;
  isSelected: boolean;
  onSelect: () => void;
  target?: TargetType;
  onTargetChange: (target: TargetType) => void;
};

type ControlProps<T extends TargetType = any> = {
  target?: T;
  onTargetChange: (target: TargetType) => void;
  params: Record<string, any>;
};

function OuteltOrServiceCategoryTarget({
  params,
  target,
  onTargetChange,
}: ControlProps<EmbeddedOutletOrServiceCategory>) {
  const loadOptions = useOptions<any>('supplier/outlet_or_service_category/', params, { suggestion: true });
  let placeholder = 'Outlet or service category';
  if (params?.kind === 'OUTLET') {
    placeholder = 'Outlet';
  } else if (params.kind === 'SERVICE_CATEGORY') {
    placeholder = 'Service category';
  }

  return (
    <Select
      name="outlet_or_service_category"
      placeholder={placeholder}
      {...outletOrServiceCategoryFieldProps}
      loadOptions={loadOptions}
      defaultOptions
      value={target}
      onChange={onTargetChange as any}
      required
    />
  );
}

function VendorTarget({ params, target, onTargetChange }: ControlProps<Vendor>) {
  const loadOptions = useOptions<any>('supplier/vendors/', params, { suggestion: true });
  return (
    <Select
      name="vendor"
      placeholder="Vendor"
      {...vendorFieldProps}
      loadOptions={loadOptions}
      defaultOptions
      value={target}
      onChange={onTargetChange as any}
      required
    />
  );
}

function AcitivtyTarget({ params, target, onTargetChange }: ControlProps<Activity>) {
  const loadOptions = useOptions<any>('core/activities/', params, { suggestion: true });
  return (
    <Select
      name="activity"
      placeholder="Activity"
      {...activityFieldProps}
      loadOptions={loadOptions}
      defaultOptions
      value={target}
      onChange={onTargetChange as any}
      required
    />
  );
}

// Consider injecting these like we do with success handlers
const CONTROL_MAP: Partial<Record<CatalogAction, Record<string, React.ComponentType<ControlProps>>>> = {
  MOVE: {
    Product: (props) => <OuteltOrServiceCategoryTarget {...props} />,
    OutletOrServiceCategory: (props) => <VendorTarget {...props} />,
  },
  CONVERT: {
    Product: (props) => <AcitivtyTarget {...props} />,
  },
  FOLD: {
    OutletOrServiceCategory: (props) => <OuteltOrServiceCategoryTarget {...props} />,
  },
};

const LABEL_MAP: Record<CatalogAction, ReactNode> = {
  MOVE: 'Move',
  REMOVE: 'Remove',
  CONVERT: 'Convert',
  FOLD: 'Fold',
};

export function ActionOption({
  action,
  error,
  params,
  model,
  isSelected,
  onSelect,
  target,
  onTargetChange,
}: ActionOptionProps) {
  const id = useId();

  const Control = CONTROL_MAP[action]?.[model] || null;

  const disabled = !!error;

  return (
    <div className={styles.container}>
      <div className={styles.optionContainerVariants({ disabled, selected: isSelected })}>
        <Checkbox
          variant="radio"
          checked={isSelected}
          onChange={onSelect}
          disabled={!!error}
          id={id}
          testId={`action-${action.toLowerCase()}-${model}`}
        />
        <label htmlFor={id} className={styles.optionLabel}>
          {LABEL_MAP[action]}
        </label>
        {Control !== null && isSelected && !disabled && (
          <Box ml="10" flexGrow={1}>
            <Control target={target} onTargetChange={onTargetChange} params={params} />
          </Box>
        )}
        {error && (
          <Box marginLeft="auto" pl="6" textAlign="right" color="warning500" textSize="xs">
            {error}
          </Box>
        )}
      </div>
    </div>
  );
}

export function DoNothingOption({ isSelected, onSelect }: { isSelected: boolean; onSelect: () => void }) {
  const id = useId();

  return (
    <div className={styles.container}>
      <div className={styles.optionContainerVariants({ selected: isSelected })}>
        <Checkbox variant="radio" checked={isSelected} onChange={onSelect} id={id} />
        <label htmlFor={id} className={styles.optionLabel}>
          Keep entity as is
        </label>
      </div>
    </div>
  );
}
